<template>
  <div class="side-by-side">
    <select
      id="selectDownload"
      :key="selectedValue"
      v-model="selectedValue"
      class="select-css auto mr-half mb-half-mobile"
    >
      <option v-for="l in listOptions" :key="l.value" :value="l.value">{{ l.description }}</option>
    </select>
    <button id="btnDownload" class="btn-primary" type="button" @click="download()">
      {{ t(`${parentComponentName}.btnDownload`) }}
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { IListOptions } from "@/common/api/unifiedPortal/interfaces";
import { useUserStore } from "@/stores/user";
import { downloadClient } from "@/common/safeClient";
import { productList } from "@/common/productList";
import { productType } from "@/globalEnums";
import { getOsFamily, getBrowserFamily } from "@/common/browserDetect";

const componentName = "SelectorComponent";
const parentComponentName = "DownloadProductComponent";

export default defineComponent({
  name: componentName,
  props: {
    productCode: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    logEvent("created", componentName, props);
    const listOptions = ref<IListOptions[]>([]);
    const selectedValue = ref<string>("");
    const userStore = useUserStore();

    const hasBestBuyS2 = computed(() => {
      return !!userStore.currentUser?.subscriberInfo?.hasBestBuyS2;
    });

    onMounted(() => {
      selectedValue.value = "";

      // Populate the list
      const currentProduct = productList().find(p => p.code == props.productCode);
      if (currentProduct) {
        currentProduct.targetSystem.forEach(item => {
          listOptions.value.push({
            value: hasBestBuyS2.value && item.bbyLink.length > 0 ? item.bbyLink : item.link,
            description: item.fullName,
          });
        });

        // Get current OS name
        const currentSystemFullName =
          currentProduct.productType === productType.Browser
            ? currentProduct.targetSystem.find(c => c.familyName.toLowerCase() === getBrowserFamily().toLowerCase())
                ?.fullName
            : currentProduct.targetSystem.find(c => c.familyName.toLowerCase() === getOsFamily().toLowerCase())
                ?.fullName;

        // Make the current OS appear first
        if (currentSystemFullName) {
          listOptions.value.sort(function (a, b) {
            return a.description == currentSystemFullName ? -1 : b.description == currentSystemFullName ? 1 : 0;
          });
        }

        selectedValue.value = listOptions.value[0].value;
      }
    });

    function download() {
      logEvent("download", componentName, selectedValue.value);
      isFile(selectedValue.value) ? downloadClient(selectedValue.value) : window.open(selectedValue.value, "_blank");
    }

    function isFile(location: string) {
      location = location.toLowerCase();
      return (
        location.includes(".exe") || location.includes(".dmg") || location.includes(".zip") || location.includes(".pkg")
      );
    }

    //Expose the template
    return {
      t,
      download,
      selectedValue,
      listOptions,
      parentComponentName,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
